<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="tw-flex tw-flex-col tw-gap-6">
    <div class="tw-flex tw-items-center tw-justify-between">
      <div :id="id" class="tw-text-[24px] tw-color-[#211F58] tw-font-semibold">
        {{ title }}
      </div>
      <div
        v-if="isProgress"
        id="pen-icon-progress"
        :class="[
          isEditProgress ? 'tw-bg-[#211F58]' : 'tw-bg-[#FFF]',
          isCallApi
            ? '!tw-cursor-not-allowed !tw-p-[5px] !tw-bg-[#211F58]'
            : '',
        ]"
        class="header__pen tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
        @click="editNotesProgress"
      >
        <Pen
          :is-call-api="isCallApi"
          :color="isEditProgress ? '#ffffff' : '#387DFF'"
        />
      </div>
      <div
        v-else
        id="pen-icon-note"
        :class="[
          isEdit ? 'tw-bg-[#211F58]' : 'tw-bg-[#FFF]',
          isCallApi
            ? '!tw-cursor-not-allowed !tw-p-[5px] !tw-bg-[#211F58]'
            : '',
        ]"
        class="header__pen tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
        @click="editNotes"
      >
        <Pen :is-call-api="isCallApi" :color="isEdit ? '#ffffff' : '#387DFF'" />
      </div>
    </div>
    <div v-if="isProgress">
      <div v-if="isLoading" class="tw-h-[304px] bg-skeleton"></div>
      <div v-else>
        <div v-if="isEditProgress" id="progress_input" :class="`tw-max-w-full`">
          <WYSiwygEditor
            v-model="processData"
            :include-image="false"
            :is-disabled="isChecklistCompleted"
            :height="'262px'"
            :custom-class="`tw-max-w-full tw-h-full`"
            :convert-html="true"
            @update:value="($event) => setEditNotesValue($event)"
          />
        </div>
        <div
          v-else
          class="tw-whitespace-pre-wrap tw-flex-1 tw-min-h-[304px] tw-overflow-y-auto tw-font-Poppins tw-text-base tw-font-normal tw-color-[#231F20] tw-opacity-70 tw-border tw-border-solid tw-rounded-md tw-border-[#EFF0F1] tw-p-[40px]"
          v-html="processData"
        ></div>
      </div>
    </div>
    <div v-else>
      <div v-if="isLoading" class="tw-h-[232px] bg-skeleton"></div>
      <div v-else>
        <div v-if="isEdit" id="note_input" :class="`tw-max-w-full`">
          <WYSiwygEditor
            v-model="noteData"
            :include-image="false"
            :is-disabled="isChecklistCompleted"
            :height="'194.33px'"
            :custom-class="`tw-max-w-full tw-h-full`"
            :convert-html="true"
            @update:value="($event) => setEditNotesValue($event)"
          />
        </div>
        <div
          v-else
          class="tw-whitespace-pre-wrap tw-flex-1 tw-min-h-[232px] tw-overflow-y-auto tw-font-Poppins tw-text-base tw-font-normal tw-color-[#231F20] tw-opacity-70 tw-border tw-border-solid tw-rounded-md tw-border-[#EFF0F1] tw-p-[40px]"
          v-html="noteData"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import Pen from "../svgs/pen.vue";
import WYSiwygEditor from "@/components/common/wysiwygEditor.vue";
import store from "@/store";

const noteData = ref("");
const processData = ref("");

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  isProgress: {
    type: Boolean,
    default: false,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  isEditProgress: {
    type: Boolean,
    default: false,
  },
  isDiscard: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:edit", "update:editProgress"]);

const isCallApi = ref(false);

const checklistSummaryReportData = computed(
  () => store.getters["Sharing/getChecklistSummary"],
);

const checklistReportId = computed(
  () => store.getters["Sharing/getChecklistReportId"],
);

let oldData;

watch(
  checklistSummaryReportData,
  (value) => {
    oldData = value;
    noteData.value = value?.checklistNotes;
    processData.value = value?.checklistProgressNotes;
  },
  {
    immediate: true,
  },
);

const editNotes = () => {
  if (isCallApi.value) return;
  emit("update:edit", !props.isEdit);
};

const editNotesProgress = () => {
  if (isCallApi.value) return;
  emit("update:editProgress", !props.isEditProgress);
};

const setEditNotesValue = (value) => {
  if (props.isProgress) {
    processData.value = value;
  } else {
    noteData.value = value;
  }
};

const updateNotesValue = async () => {
  try {
    isCallApi.value = true;
    const res = await store.dispatch("Sharing/updateChecklistNote", {
      checklistId: checklistReportId.value,
      params: props.isProgress
        ? { checklistProgressNotes: processData.value }
        : {
            checklistNotes: noteData.value,
          },
    });
    if (!res) {
      noteData.value = oldData?.checklistNotes;
      processData.value = oldData?.checklistProgressNotes;
    }
  } catch (error) {
    console.log(error);
  } finally {
    isCallApi.value = false;
  }
};

watch(
  () => props.isEdit,
  (value) => {
    if (!value && !props.isDiscard) {
      updateNotesValue();
    }
  },
);

watch(
  () => props.isEditProgress,
  (value) => {
    if (!value && !props.isDiscard) {
      updateNotesValue();
    }
  },
);
</script>

<style scoped>
:deep(code) {
  background: #c7c7c780;
  padding: 2px;
}
.header__pen {
  border-radius: 6px;
  border: 1px solid var(--UI-rule, #eff0f1);
  padding: 9px 14px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.05);
  width: 46px;
  height: 44px;
}
.bg-skeleton {
  background: #eee;
  background: linear-gradient(90deg, #ffffff 10%, #e9e9e9 25%, #ffffff 35%);
  background-size: 200% 100%;
  animation: 1.5s custom-skeleton-loading linear infinite;
}

@media print {
  #pen-icon-note,
  #pen-icon-progress {
    display: none;
  }
}
</style>
